import { useMemo, useState, type ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSmartNavigate } from '@/lib/nav-prefs/use-smart-navigate';
import { useTranslation } from 'react-i18next';
import { useArticle, useArticleCategories, useArticleFamilies, useArticleBrands } from '@/hooks/use-articles';
import { useSalesDocuments } from '@/hooks/use-sales';
import { usePurchaseDocuments } from '@/hooks/use-purchases';
import { useAllStock, useWarehouses, useStockMovements } from '@/hooks/use-stock';
import { useClients } from '@/hooks/use-clients';
import { useSuppliers } from '@/hooks/use-suppliers';
import { Card, CardContent } from '@erp/ui';
import { Badge } from '@erp/ui';
import { Button } from '@erp/ui';
import { FilterBar, FilterField } from '@erp/ui';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@erp/ui';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@erp/ui';
import { ArrowRight, Pencil } from '@/components/icons';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { ArticleFormDialog } from './ArticleFormDialog';
import { resolveArticleCategorySelection, resolveOptionSelection } from './article-category-utils';
import type { Article, ArticleBrand, ArticleCategory, ArticleFamily } from '@erp/data-access/interfaces';
import { formatQuantity } from '@/lib/format';

function formatPrice(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  return n.toLocaleString('fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}
function formatDate(d: string): string {
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
}

const SALES_DOC_LABELS: Record<string, string> = {
  quote: 'sales.quote', order: 'sales.order', delivery: 'sales.delivery',
  invoice: 'sales.invoice', credit_note: 'sales.creditNote',
};
const SALES_DOC_PATHS: Record<string, string> = {
  quote: '/sales/quotes', order: '/sales/orders', delivery: '/sales/deliveries',
  invoice: '/sales/invoices', credit_note: '/sales/credit-notes',
};
const SALES_DOC_TYPES_ORDER = ['quote', 'order', 'delivery', 'invoice', 'credit_note'] as const;
const PURCHASE_DOC_LABELS: Record<string, string> = {
  order: 'purchases.order', reception: 'purchases.reception',
  invoice: 'purchases.invoice', credit_note: 'purchases.creditNote',
};
const PURCHASE_DOC_PATHS: Record<string, string> = {
  order: '/purchases/orders', reception: '/purchases/receptions',
  invoice: '/purchases/invoices', credit_note: '/purchases/credit-notes',
};

export function ArticleProfilePage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useSmartNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [salesDocTypeFilter, setSalesDocTypeFilter] = useState('');
  const [salesClientFilter, setSalesClientFilter] = useState('');

  const { data: article, isLoading } = useArticle(id ?? '');
  const { data: categories } = useArticleCategories();
  const { data: families } = useArticleFamilies();
  const { data: brands } = useArticleBrands();
  const { data: clients } = useClients();
  const { data: suppliers } = useSuppliers();
  const { data: salesDocs } = useSalesDocuments({});
  const { data: purchaseDocs } = usePurchaseDocuments({});
  const { data: allStock } = useAllStock();
  const { data: warehouses } = useWarehouses();
  const { data: movements } = useStockMovements(id);

  const clientMap = useMemo(() => {
    const map = new Map<string, string>();
    clients?.forEach((c) => map.set(c.id, c.name));
    return map;
  }, [clients]);
  const supplierMap = useMemo(() => {
    const map = new Map<string, string>();
    suppliers?.forEach((s) => map.set(s.id, s.name));
    return map;
  }, [suppliers]);

  const linkedSales = useMemo(() => {
    if (!salesDocs || !id) return [];
    return salesDocs.filter((d) => d.lines?.some((l) => l.articleId === id));
  }, [salesDocs, id]);
  const filteredLinkedSales = useMemo(() => linkedSales.filter((doc) => {
    if (salesDocTypeFilter && doc.docType !== salesDocTypeFilter) return false;
    if (salesClientFilter && doc.clientId !== salesClientFilter) return false;
    return true;
  }), [linkedSales, salesClientFilter, salesDocTypeFilter]);
  const linkedSalesDocTypeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const doc of linkedSales) counts[doc.docType] = (counts[doc.docType] ?? 0) + 1;
    return counts;
  }, [linkedSales]);
  const linkedSalesDocTypeOptions = useMemo(
    () => SALES_DOC_TYPES_ORDER
      .filter((type) => (linkedSalesDocTypeCounts[type] ?? 0) > 0)
      .map((type) => ({
        value: type,
        label: `${t(SALES_DOC_LABELS[type]!)} (${linkedSalesDocTypeCounts[type] ?? 0})`,
      })),
    [linkedSalesDocTypeCounts, t],
  );
  const linkedSalesClientOptions = useMemo(() => {
    const options = new Map<string, string>();
    for (const doc of linkedSales) {
      const label = clientMap.get(doc.clientId);
      if (label) options.set(doc.clientId, label);
    }
    return Array.from(options, ([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [clientMap, linkedSales]);
  const linkedPurchases = useMemo(() => {
    if (!purchaseDocs || !id) return [];
    return purchaseDocs.filter((d) => d.lines?.some((l) => l.articleId === id));
  }, [purchaseDocs, id]);

  const stockByWarehouse = useMemo(() => {
    const map = new Map<string, number>();
    if (!allStock || !id) return map;
    for (const s of allStock) {
      if (s.articleId === id) map.set(s.warehouseId, (map.get(s.warehouseId) ?? 0) + s.quantity);
    }
    return map;
  }, [allStock, id]);
  const totalStock = useMemo(() => {
    let total = 0;
    stockByWarehouse.forEach((q) => { total += q; });
    return total;
  }, [stockByWarehouse]);

  const salesStats = useMemo(() => {
    let totalQtySold = 0, totalRevenue = 0, totalBenefit = 0;
    for (const doc of linkedSales) {
      if (doc.docType !== 'invoice' && doc.docType !== 'delivery') continue;
      for (const line of doc.lines ?? []) {
        if (line.articleId !== id) continue;
        totalQtySold += line.quantity;
        totalRevenue += line.totalHt;
        totalBenefit += line.totalHt - (line.purchasePrice * line.quantity);
      }
    }
    return { totalQtySold, totalRevenue, totalBenefit };
  }, [linkedSales, id]);

  const purchaseStats = useMemo(() => {
    let totalQtyBought = 0, totalCost = 0;
    for (const doc of linkedPurchases) {
      if (doc.docType !== 'invoice' && doc.docType !== 'reception') continue;
      for (const line of doc.lines ?? []) {
        if (line.articleId !== id) continue;
        totalQtyBought += line.quantity;
        totalCost += line.totalHt;
      }
    }
    return { totalQtyBought, totalCost };
  }, [linkedPurchases, id]);

  if (isLoading && !article) {
    return <div className="flex items-center justify-center py-12 text-muted-foreground">{t('common.loading')}</div>;
  }
  if (!article) {
    return <div className="flex items-center justify-center py-12 text-muted-foreground">{t('common.noResults')}</div>;
  }

  const primaryImage = article.imageUrl ?? article.images?.[0] ?? null;

  return (
    <div className="space-y-4">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 -mx-4 px-4 py-3 bg-background/95 backdrop-blur border-b">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            {primaryImage ? (
              <img src={primaryImage} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center text-xl font-bold text-muted-foreground/30 shrink-0">
                {article.name.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <h2 className="text-xl font-bold truncate">{article.name}</h2>
              <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
                <span>{article.code}</span>
                {article.barcode && <span>· {article.barcode}</span>}
                {article.isActive
                  ? <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-400">{t('common.active')}</Badge>
                  : <Badge variant="outline" className="text-muted-foreground">{t('common.inactive')}</Badge>}
                {article.isInvoiceable && (
                  <Badge variant="outline" className="border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-400">{t('articles.isInvoiceable')}</Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <Pencil size={14} className="mr-1" />
              {t('common.edit')}
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/articles')}>
              {t('common.back')}
            </Button>
          </div>
        </div>

        {/* Compact KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-3">
          <Kpi label={t('stock.currentStock')} value={`${totalStock} ${t(`articles.units.${article.unit}`)}`} alert={totalStock <= article.stockMin} />
          <Kpi label={t('articles.profile.qtySold')} value={String(salesStats.totalQtySold)} />
          <Kpi label={t('articles.profile.revenue')} value={`${formatPrice(salesStats.totalRevenue)} DT`} />
          <Kpi label={t('articles.profile.benefit')} value={`${formatPrice(salesStats.totalBenefit)} DT`} positive={salesStats.totalBenefit >= 0} />
          <Kpi label={t('articles.profile.qtyBought')} value={String(purchaseStats.totalQtyBought)} />
        </div>
      </div>

      <Tabs defaultValue="details">
        <TabsList>
          <TabsTrigger value="details">{t('articles.profile.tabs.details')}</TabsTrigger>
          <TabsTrigger value="stock">{t('articles.profile.tabs.stock')}</TabsTrigger>
          <TabsTrigger value="sales">{t('articles.profile.tabs.sales')} ({linkedSales.length})</TabsTrigger>
          <TabsTrigger value="purchases">{t('articles.profile.tabs.purchases')} ({linkedPurchases.length})</TabsTrigger>
        </TabsList>

        {/* Tab 1 - read-only details */}
        <TabsContent value="details">
          <ArticleDetails
            article={article}
            categories={categories ?? []}
            families={families ?? []}
            brands={brands ?? []}
            totalStock={totalStock}
          />
        </TabsContent>

        {/* Tab 2 — Stock */}
        <TabsContent value="stock">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardContent className="pt-5 space-y-3">
                <h3 className="text-sm font-semibold">{t('articles.profile.stockByWarehouse')}</h3>
                {warehouses && warehouses.length > 0 ? (
                  <div className="rounded-lg border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('stock.warehouseName')}</TableHead>
                          <TableHead className="text-end">{t('stock.quantity')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {warehouses.map((w) => {
                          const qty = stockByWarehouse.get(w.id) ?? 0;
                          return (
                            <TableRow key={w.id}>
                              <TableCell>{w.name}</TableCell>
                              <TableCell className={`text-end font-medium ${qty <= article.stockMin ? 'text-destructive' : ''}`}>{qty}</TableCell>
                            </TableRow>
                          );
                        })}
                        <TableRow>
                          <TableCell className="font-bold">{t('common.total')}</TableCell>
                          <TableCell className="text-end font-bold">{totalStock}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">{t('stock.noWarehouses')}</p>
                )}
                {article.stockMin > 0 && (
                  <p className="text-xs text-muted-foreground">{t('articles.stockMin')}: {article.stockMin}</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-5 space-y-3">
                <h3 className="text-sm font-semibold">{t('stock.recentMovements')}</h3>
                <div className="rounded-lg border max-h-[420px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('stock.movementType')}</TableHead>
                        <TableHead>{t('stock.warehouses')}</TableHead>
                        <TableHead>{t('sales.docDate')}</TableHead>
                        <TableHead className="text-end">{t('stock.quantity')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {!movements?.length ? (
                        <TableRow><TableCell colSpan={4} className="text-center py-6 text-muted-foreground">{t('common.noResults')}</TableCell></TableRow>
                      ) : movements.slice(0, 30).map((m) => {
                        const wh = warehouses?.find((w) => w.id === m.warehouseId);
                        return (
                          <TableRow key={m.id}>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={
                                  m.movementType === 'in'
                                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-400'
                                    : m.movementType === 'out'
                                    ? 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-400'
                                    : 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-400'
                                }
                              >
                                {t(`stock.movement${m.movementType.charAt(0).toUpperCase() + m.movementType.slice(1)}`)}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm">{wh?.name ?? '—'}</TableCell>
                            <TableCell>{formatDate(m.movementDate)}</TableCell>
                            <TableCell className="text-end font-medium">
                              <span className={m.quantity > 0 ? 'text-green-600' : m.quantity < 0 ? 'text-destructive' : ''}>
                                {m.quantity > 0 ? '+' : ''}{m.quantity}
                              </span>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab 3 — Sales */}
        <TabsContent value="sales">
          <Card>
            <CardContent className="pt-5">
              {(linkedSalesDocTypeOptions.length > 1 || salesDocTypeFilter || linkedSalesClientOptions.length > 1 || salesClientFilter) && (
                <FilterBar>
                  {(linkedSalesDocTypeOptions.length > 1 || salesDocTypeFilter) && (
                    <FilterField label={t('dashboard.type')} minWidth={220}>
                      <SearchableSelect
                        options={linkedSalesDocTypeOptions}
                        value={salesDocTypeFilter}
                        onChange={setSalesDocTypeFilter}
                        label={t('dashboard.type')}
                        placeholder={t('common.all')}
                        searchPlaceholder={t('common.search')}
                        className="h-8"
                      />
                    </FilterField>
                  )}
                  {(linkedSalesClientOptions.length > 1 || salesClientFilter) && (
                    <FilterField label={t('sales.client')} minWidth={240}>
                      <SearchableSelect
                        options={linkedSalesClientOptions}
                        value={salesClientFilter}
                        onChange={setSalesClientFilter}
                        label={t('sales.client')}
                        placeholder={t('common.all')}
                        searchPlaceholder={t('common.search')}
                        className="h-8"
                      />
                    </FilterField>
                  )}
                </FilterBar>
              )}
              <div className="rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('dashboard.type')}</TableHead>
                      <TableHead>{t('sales.number')}</TableHead>
                      <TableHead>{t('sales.client')}</TableHead>
                      <TableHead>{t('sales.docDate')}</TableHead>
                      <TableHead className="text-end">{t('sales.quantity')}</TableHead>
                      <TableHead className="text-end">{t('sales.totalHt')}</TableHead>
                      <TableHead className="w-8" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLinkedSales.length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="text-center py-6 text-muted-foreground">{t('common.noResults')}</TableCell></TableRow>
                    ) : filteredLinkedSales.map((doc) => {
                      const line = doc.lines?.find((l) => l.articleId === id);
                      return (
                        <TableRow key={doc.id}>
                          <TableCell><Badge variant="outline">{t(SALES_DOC_LABELS[doc.docType] ?? doc.docType)}</Badge></TableCell>
                          <TableCell className="font-medium">{doc.number}</TableCell>
                          <TableCell className="text-sm">{clientMap.get(doc.clientId) ?? '—'}</TableCell>
                          <TableCell>{formatDate(doc.docDate)}</TableCell>
                          <TableCell className="text-end">{line?.quantity ?? '—'}</TableCell>
                          <TableCell className="text-end">{line ? `${formatPrice(line.totalHt)} DT` : '—'}</TableCell>
                          <TableCell>
                            <Link to={`${SALES_DOC_PATHS[doc.docType]}/${doc.id}`}>
                              <Button variant="ghost" size="icon-xs"><ArrowRight size={14} /></Button>
                            </Link>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 4 — Purchases */}
        <TabsContent value="purchases">
          <Card>
            <CardContent className="pt-5">
              <div className="rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('dashboard.type')}</TableHead>
                      <TableHead>{t('sales.number')}</TableHead>
                      <TableHead>{t('purchases.supplier')}</TableHead>
                      <TableHead>{t('sales.docDate')}</TableHead>
                      <TableHead className="text-end">{t('sales.quantity')}</TableHead>
                      <TableHead className="text-end">{t('sales.totalHt')}</TableHead>
                      <TableHead className="w-8" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {linkedPurchases.length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="text-center py-6 text-muted-foreground">{t('common.noResults')}</TableCell></TableRow>
                    ) : linkedPurchases.map((doc) => {
                      const line = doc.lines?.find((l) => l.articleId === id);
                      return (
                        <TableRow key={doc.id}>
                          <TableCell><Badge variant="outline">{t(PURCHASE_DOC_LABELS[doc.docType] ?? doc.docType)}</Badge></TableCell>
                          <TableCell className="font-medium">{doc.number}</TableCell>
                          <TableCell className="text-sm">{supplierMap.get(doc.supplierId) ?? '—'}</TableCell>
                          <TableCell>{formatDate(doc.docDate)}</TableCell>
                          <TableCell className="text-end">{line?.quantity ?? '—'}</TableCell>
                          <TableCell className="text-end">{line ? `${formatPrice(line.totalHt)} DT` : '—'}</TableCell>
                          <TableCell>
                            <Link to={`${PURCHASE_DOC_PATHS[doc.docType]}/${doc.id}`}>
                              <Button variant="ghost" size="icon-xs"><ArrowRight size={14} /></Button>
                            </Link>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <ArticleFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        article={article}
      />
    </div>
  );
}

function ArticleDetails({
  article,
  categories,
  families,
  brands,
  totalStock,
}: {
  article: Article;
  categories: ArticleCategory[];
  families: ArticleFamily[];
  brands: ArticleBrand[];
  totalStock: number;
}) {
  const { t } = useTranslation();
  const resolvedCategories = resolveArticleCategorySelection(article, categories);
  const parentCategory = categories.find((c) => c.id === resolvedCategories.categoryId);
  const subCategory = categories.find((c) => c.id === resolvedCategories.subFamily);
  const familyId = resolveOptionSelection(article.family, families);
  const brandId = resolveOptionSelection(article.brand, brands);
  const family = families.find((f) => f.id === familyId);
  const brand = brands.find((b) => b.id === brandId);
  const purchasePriceNet = round3(article.purchasePriceHt * (1 - article.supplierDiscount / 100));
  const purchasePriceTtc = round3(purchasePriceNet * (1 + article.vatRate / 100));
  const margin = purchasePriceNet > 0
    ? round3(((article.salePriceHt - purchasePriceNet) / purchasePriceNet) * 100)
    : 0;
  const benefit = round3(article.salePriceHt - purchasePriceNet);
  const images = article.images?.length ? article.images : article.imageUrl ? [article.imageUrl] : [];

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-4">
      <div className="space-y-4">
        <DetailSection title={t('articles.sections.identification')}>
          <DetailItem label={t('articles.code')} value={article.code} />
          <DetailItem label={t('articles.barcode')} value={article.barcode} />
          <DetailItem label={t('articles.name')} value={article.name} wide />
          <DetailItem label={t('articles.description')} value={article.description} wide />
        </DetailSection>

        <DetailSection title={t('articles.sections.classification')}>
          <DetailItem label={t('articles.category')} value={parentCategory?.name ?? resolvedCategories.categoryId ?? t('articles.noCategory')} />
          <DetailItem label={t('articles.subCategory')} value={subCategory?.name ?? resolvedCategories.subFamily} />
          <DetailItem label={t('articles.family')} value={family?.name ?? article.family} />
          <DetailItem label={t('articles.brand')} value={brand?.name ?? article.brand} />
          <DetailItem label={t('articles.unit')} value={t(`articles.units.${article.unit}`)} />
        </DetailSection>

        <DetailSection title={t('articles.sections.purchasePricing')}>
          <DetailItem label={t('articles.purchasePriceHt')} value={`${formatPrice(article.purchasePriceHt)} DT`} />
          <DetailItem label={t('articles.supplierDiscount')} value={`${formatPrice(article.supplierDiscount)} %`} />
          <DetailItem label={t('articles.vatRate')} value={`${formatPrice(article.vatRate)} %`} />
          <DetailItem label={t('articles.purchasePriceNet')} value={`${formatPrice(purchasePriceNet)} DT`} />
          <DetailItem label={t('articles.purchasePriceTtc')} value={`${formatPrice(purchasePriceTtc)} DT`} />
        </DetailSection>

        <DetailSection title={t('articles.sections.salePricing')}>
          <DetailItem label={t('articles.salePriceHt')} value={`${formatPrice(article.salePriceHt)} DT`} />
          <DetailItem label={t('articles.salePriceTtc')} value={`${formatPrice(article.salePriceTtc)} DT`} />
          <DetailItem label={`${t('articles.margin')} %`} value={`${formatPrice(margin)} %`} />
          <DetailItem label={t('articles.benefitAmount')} value={`${formatPrice(benefit)} DT`} />
        </DetailSection>

        <DetailSection title={t('articles.sections.stock')}>
          <DetailItem label={t('stock.currentStock')} value={`${formatQuantity(totalStock)} ${t(`articles.units.${article.unit}`)}`} />
          <DetailItem label={t('articles.stockMin')} value={`${formatQuantity(article.stockMin)} ${t(`articles.units.${article.unit}`)}`} />
          <DetailItem label={t('articles.initialStock')} value={`${formatQuantity(article.initialStock)} ${t(`articles.units.${article.unit}`)}`} />
        </DetailSection>
      </div>

      <div className="space-y-4">
        <DetailSection title={t('articles.sections.status')}>
          <DetailItem
            label={t('articles.isActive')}
            value={article.isActive ? (
              <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-400">{t('common.active')}</Badge>
            ) : (
              <Badge variant="outline" className="text-muted-foreground">{t('common.inactive')}</Badge>
            )}
          />
          <DetailItem
            label={t('articles.isInvoiceable')}
            value={article.isInvoiceable ? (
              <Badge variant="outline" className="border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-400">{t('common.yes')}</Badge>
            ) : (
              <Badge variant="outline" className="text-muted-foreground">{t('common.no')}</Badge>
            )}
          />
        </DetailSection>

        <DetailSection title={t('articles.images')}>
          {images.length > 0 ? (
            <div className="grid grid-cols-2 gap-3">
              {images.map((src, idx) => (
                <img
                  key={`${src}-${idx}`}
                  src={src}
                  alt=""
                  className="aspect-square w-full rounded-md border object-cover"
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{t('articles.noImages')}</p>
          )}
        </DetailSection>
      </div>
    </div>
  );
}

function DetailSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card>
      <CardContent className="pt-5">
        <h3 className="mb-4 text-sm font-semibold">{title}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
      </CardContent>
    </Card>
  );
}

function DetailItem({ label, value, wide }: { label: string; value: ReactNode; wide?: boolean }) {
  const empty = value === null || value === undefined || value === '';
  return (
    <div className={wide ? 'md:col-span-2' : undefined}>
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <div className="mt-1 min-h-6 text-sm font-medium text-foreground whitespace-pre-wrap break-words">
        {empty ? '-' : value}
      </div>
    </div>
  );
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

function Kpi({ label, value, alert, positive }: { label: string; value: string; alert?: boolean; positive?: boolean }) {
  const tone = alert ? 'text-destructive' : positive === false ? 'text-destructive' : positive === true ? 'text-green-600' : '';
  return (
    <div className="rounded-md border bg-card px-3 py-2">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`text-base font-bold leading-tight mt-0.5 ${tone}`}>{value}</p>
    </div>
  );
}
